/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import clsx from 'clsx';

import { classNameForButtonProps } from '../../utils/buttonUtils';

export interface ButtonSizes {
  size?: 'small' | 'medium' | 'large';
}

export type ButtonVariant =
  | 'danger'
  | 'primary'
  | 'secondary'
  | 'borderless'
  | 'important'
  | 'selected'
  | 'light'
  | 'unselected'
  | 'success';

export interface IButton {
  active?: boolean;
  // Prefer data-testid for new changes as @testing-library/react has a shortcut
  'data-testid'?: string;
  dataFor?: string;
  dataTip?: string;
  disabled?: boolean;
  // Increase button height for mobile for text on multiple lines
  isMultilineText?: boolean;
  modifierClassName?: string;
  stretch?: boolean;
  variant?: ButtonVariant;
  size?: 'small' | 'medium' | 'large';
  shape?: 'rounded';
  title?: string;
  dataValue?: string;
  onPointerDown?(event: React.MouseEvent<HTMLElement>): any;
  onClick?(event: React.MouseEvent<HTMLElement>): any;
  onKeyPress?(event: React.KeyboardEvent): any;
  trackingInformation?: TrackingInformation;
}

interface Props extends IButton {
  children?: React.ReactNode;
  type?: 'button' | 'submit';
  innerRef?: React.Ref<HTMLButtonElement>;
}

/**
 * @deprecated Succeeded by ui/Button (dashboard) and bamboo/Button (consumer)
 *
 * - [Dashboard Migration Guide](https://github.com/tocktix/admin/wiki/Design-System:-Dashboard-Button-Migration-Guide)
 * - [Dashboard Storybook](http://tock-assets.tocktix.com/styleguide/dashboard/?path=/story/components-buttons--mui-button-variants)
 * - [Consumer Storybook](http://tock-assets.tocktix.com/styleguide/consumer/index.html?path=/story/bamboo-button-button--base)
 */
export class Button extends React.PureComponent<Props> {
  static defaultProps = {
    type: 'button',
  };

  constructor(props: Props) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(event: React.MouseEvent<HTMLElement>) {
    if (this.props.onClick) {
      this.props.onClick(event);
    }
    if (this.props.trackingInformation && window.analytics) {
      const { category = 'button', action = 'click', label } = this.props.trackingInformation;
      window.analytics.sendEvent(category, action, label, null, {
        transport: 'beacon',
      });
    }
  }

  render() {
    // doing the rest variable here allows to pass more semantic looking html attributes like data-tip or aria-whatever
    // putting it after the other data attributes for backwards compat for now.
    const {
      active,
      children,
      dataFor,

      dataTip,

      disabled,
      isMultilineText,
      innerRef,
      type,
      onPointerDown,
      onClick,
      onKeyPress,
      modifierClassName,
      variant,
      dataValue,
      stretch,
      title,
      trackingInformation,
      ...rest
    } = this.props;

    return (
      <button
        data-for={dataFor}
        data-tip={dataTip}
        data-testid={this.props['data-testid']}
        data-value={dataValue}
        title={title}
        aria-label={title}
        ref={innerRef}
        {...rest}
        className={clsx(classNameForButtonProps(this.props), {
          'Button-multilineText': isMultilineText,
        })}
        disabled={disabled}
        type={type}
        onPointerDown={onPointerDown}
        onClick={this.handleClick}
        onKeyPress={onKeyPress}
      >
        <div className="Button-content">{children}</div>
      </button>
    );
  }
}

/**
 * @deprecated Succeeded by ui/Button (dashboard) and bamboo/Button (consumer)
 *
 * - [Dashboard Migration Guide](https://github.com/tocktix/admin/wiki/Design-System:-Dashboard-Button-Migration-Guide)
 * - [Dashboard Storybook](http://tock-assets.tocktix.com/styleguide/dashboard/?path=/story/components-buttons--mui-button-variants)
 * - [Consumer Storybook](http://tock-assets.tocktix.com/styleguide/consumer/index.html?path=/story/bamboo-button-button--base)
 */
export default React.forwardRef<HTMLButtonElement, Props>((props, ref) => (
  <Button innerRef={ref} {...props} />
));
